import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import {
  Alert,
  Box,
  Button,
  Chip,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useMemo, useState } from "react";

import { INSERT_MERGE_TAG_COMMAND } from "./editor/mergeTagPlugin";
import { useCaretInDiscoverContext } from "./editor/useCaretInDiscoverContext";
import {
  getMergeTagByIdentifier,
  MERGE_TAG_PATTERN,
  parseMergeTagSlot,
} from "./mergeTagDefinitions";
import { DISCOVER_HEADING_TAG } from "./productContexts";
import { useTextComposerMergeTagCategories } from "./useConnectorMergeTags";
import { SectionAccordion } from "../../compose/SectionAccordion";
import { SectionChipRow } from "../../compose/SectionAccordion.styles";
import type { ExtensionRenderProps } from "../../editor/extensionContract";

import { SearchIcon, TagIcon } from "@/icons";

function extractUsedMergeTagLabels(bodyText: string | undefined): string[] {
  if (!bodyText) return [];
  const regex = new RegExp(MERGE_TAG_PATTERN.source, "g");
  const seen = new Set<string>();
  const labels: string[] = [];
  let match: RegExpExecArray | null;
  while ((match = regex.exec(bodyText)) !== null) {
    const parsed = parseMergeTagSlot(match[1]);
    const identifier = parsed?.identifier ?? match[1].trim();
    if (seen.has(identifier)) continue;
    seen.add(identifier);
    const entry = getMergeTagByIdentifier(identifier);
    labels.push(entry?.label ?? identifier);
  }
  return labels;
}

export function MergeTagsSection({ authoringState }: ExtensionRenderProps) {
  const [expanded, setExpanded] = useState(false);
  const [search, setSearch] = useState("");
  const [editor] = useLexicalComposerContext();
  const categories = useTextComposerMergeTagCategories();
  const inDiscover = useCaretInDiscoverContext();

  const filteredCategories = useMemo(() => {
    const query = search.trim().toLowerCase();
    const headingTag = `{{${DISCOVER_HEADING_TAG}}}`;
    return categories
      .map((category) => ({
        ...category,
        tags: category.tags.filter(
          (tag) =>
            // The Discover headline only resolves inside a Discover section.
            (inDiscover || tag.tag !== headingTag) &&
            (!query ||
              tag.label.toLowerCase().includes(query) ||
              tag.tag.toLowerCase().includes(query)),
        ),
      }))
      .filter((category) => category.tags.length > 0);
  }, [categories, search, inDiscover]);

  const usedTagLabels = useMemo(
    () => extractUsedMergeTagLabels(authoringState.bodyText),
    // categories: connector registry syncs when installed tags load; re-resolve labels.
    [authoringState.bodyText, categories],
  );

  const chips =
    usedTagLabels.length > 0 ? (
      <SectionChipRow>
        {usedTagLabels.map((label) => (
          <Chip key={label} label={label} size="small" color="primary" />
        ))}
      </SectionChipRow>
    ) : undefined;

  const handleInsertMergeTag = (
    tagRaw: string,
    tagLabel: string,
    defaultFilters?: import("@/services/mergeTags").ResolvedFilters,
  ) => {
    const identifier = tagRaw.replace(/^\{\{|\}\}$/g, "").trim();
    editor.focus(() => {
      editor.dispatchCommand(INSERT_MERGE_TAG_COMMAND, {
        tag: identifier,
        label: tagLabel,
        filters: defaultFilters,
      });
    });
  };

  return (
    <SectionAccordion
      title="Merge Tags"
      icon={TagIcon}
      iconFontSize={22}
      chips={chips}
      summaryId="text-composer-merge-tags-header"
      detailsId="text-composer-merge-tags-content"
      expanded={expanded}
      onExpandedChange={setExpanded}
    >
      <Alert severity="info" variant="outlined">
        <Typography variant="body2">
          Merge tags will be replaced with actual customer data when sent.
        </Typography>
      </Alert>
      <TextField
        variant="outlined"
        size="small"
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        slotProps={{
          root: { sx: { width: "100%" } },
          input: {
            startAdornment: <SearchIcon sx={{ mr: 2 }} />,
            placeholder: "Search merge tags",
            autoComplete: "off",
          },
          htmlInput: { autoCorrect: "off", spellCheck: false },
        }}
      />
      <Stack gap={2} sx={{ maxHeight: 360, overflowY: "auto" }}>
        {filteredCategories.length === 0 ? (
          <Typography variant="body2" sx={{ p: 3 }}>
            No merge tags match “{search}”.
          </Typography>
        ) : null}
        {filteredCategories.map((category) => (
          <Stack
            key={category.id}
            gap={1}
            sx={{ backgroundColor: "common.white", borderRadius: 1, p: 3 }}
          >
            <Typography variant="subtitle2">{category.label}</Typography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                gap: 3,
              }}
            >
              {category.tags.map((tag) => (
                <Button
                  key={tag.tag}
                  type="button"
                  variant="text"
                  color="inherit"
                  fullWidth
                  aria-label={`Insert merge tag ${tag.label}`}
                  onClick={() => {
                    handleInsertMergeTag(
                      tag.tag,
                      tag.label,
                      tag.defaultFilters,
                    );
                  }}
                  sx={(theme) => ({
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    justifyContent: "space-between",
                    gap: 2,
                    p: 3,
                    minWidth: 0,
                    textAlign: "left",
                    textTransform: "none",
                    borderRadius: 1,
                    backgroundColor:
                      theme.palette.background["paper-elevation-0"],
                    boxShadow: "none",
                    "&:hover": {
                      backgroundColor:
                        theme.palette.background["paper-elevation-1"],
                      boxShadow: "none",
                    },
                  })}
                >
                  <Typography variant="body1" sx={{ minWidth: 0 }}>
                    {tag.label}
                  </Typography>
                  {category.id === "product-data" ? null : (
                    <Typography variant="body3" sx={{ wordBreak: "break-all" }}>
                      {tag.tag}
                    </Typography>
                  )}
                </Button>
              ))}
            </Box>
          </Stack>
        ))}
      </Stack>
    </SectionAccordion>
  );
}
